import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { KanbanSquare, CalendarDays, ShoppingCart, FileText, UtensilsCrossed, BookUser, PawPrint, Car, CreditCard, Sparkles, Trophy, Home, ArrowRight, Check, ChevronLeft, ChevronRight, HeartPulse, DollarSign, Vote } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAnnual, setIsAnnual] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const SLIDES = [
    { tier: 'FREE',    title: t('landing.slide_tasks_title', 'Tasks & Kanban Board'),    desc: t('landing.slide_tasks_desc', 'Drag-and-drop task management with priorities, due dates, and recurring tasks.'),     Icon: KanbanSquare },
    { tier: 'FREE',    title: t('landing.slide_calendar_title', 'Shared Calendar'),      desc: t('landing.slide_calendar_desc', 'Unified household calendar with travel buffer detection and commute departure chips.'), Icon: CalendarDays },
    { tier: 'FREE',    title: t('landing.slide_grocery_title', 'Grocery Lists'),         desc: t('landing.slide_grocery_desc', 'Smart grocery tracking with auto-categorization and pantry levels.'),                 Icon: ShoppingCart },
    { tier: 'BASIC',   title: t('landing.slide_meal_title', 'Meal Planning'),            desc: t('landing.slide_meal_desc', 'Weekly meal planner with recipe library and one-tap add-to-grocery.'),                   Icon: UtensilsCrossed },
    { tier: 'BASIC',   title: t('landing.slide_contacts_title', 'Contacts & Services'), desc: t('landing.slide_contacts_desc', 'Household contacts organized by service provider type.'),                             Icon: BookUser },
    { tier: 'BASIC',   title: t('landing.slide_pets_title', 'Pet Management'),           desc: t('landing.slide_pets_desc', 'Pet profiles, care schedules, and medical record tracking.'),                            Icon: PawPrint },
    { tier: 'BASIC',   title: t('landing.slide_vehicles_title', 'Vehicle Tracking'),     desc: t('landing.slide_vehicles_desc', 'Vehicle garage with service history and mileage logging.'),                          Icon: Car },
    { tier: 'BASIC',   title: t('landing.slide_bills_title', 'Bills & Subscriptions'),  desc: t('landing.slide_bills_desc', 'Subscription tracking with monthly totals and payment reminders.'),                     Icon: CreditCard },
    { tier: 'BASIC',   title: t('landing.slide_health_title', 'Health & Medications'),  desc: t('landing.slide_health_desc', 'Medication schedules, dose reminders, and auto-populated appointment history.'),        Icon: HeartPulse },
    { tier: 'BASIC',   title: t('landing.slide_finance_title', 'Financial Ledger'),     desc: t('landing.slide_finance_desc', 'Income tracking, savings goals, TVM projections, and net surplus dashboard.'),        Icon: DollarSign },
    { tier: 'PREMIUM', title: t('landing.slide_ai_title', 'AI Assistant'),              desc: t('landing.slide_ai_desc', 'Gemini-powered AI across every module — chore plans, meal generation, and more.'),        Icon: Sparkles },
    { tier: 'PREMIUM', title: t('landing.slide_feedback_title', 'Feature Polls & Feedback'), desc: t('landing.slide_feedback_desc', 'Vote on upcoming features, report bugs, and shape the product roadmap directly.'), Icon: Vote },
  ];

  const startAutoplay = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % SLIDES.length);
    }, 4000);
  };

  useEffect(() => {
    startAutoplay();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Nav */}
      <header className="sticky top-0 z-50 w-full bg-white/90 dark:bg-slate-950/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/50">
        <nav className="flex items-center justify-between px-8 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 font-black text-2xl tracking-tighter cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="p-1.5 rounded-xl bg-brand text-white shadow-lg">
              <Home className="w-6 h-6" />
            </div>
            <span>Everyday<span className="text-brand">OS</span></span>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-800 text-brand shadow-sm text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <Globe className="w-3.5 h-3.5" />
                <span className="uppercase">{i18n.language.split('-')[0]}</span>
              </button>
              {langMenuOpen && (
                <div className="absolute top-full mt-2 right-0 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-100 dark:border-slate-700 py-1 z-50 min-w-[120px]">
                  {[{ code: 'en', label: 'English' }, { code: 'fr', label: 'Français' }, { code: 'es', label: 'Español' }].map(({ code, label }) => (
                    <button
                      key={code}
                      onClick={() => { i18n.changeLanguage(code); setLangMenuOpen(false); }}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={() => navigate('/login')} className="bg-brand text-white hover:bg-brand-hover">
              {t('nav.login', 'Log In')}
            </Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="px-8 pt-20 pb-16 max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 text-brand text-xs font-black uppercase tracking-widest mb-8">
          <Sparkles className="w-4 h-4" /> {t('landing.hero_tag', 'The Standalone Household Protocol')}
        </div>
        <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.85]">
          {t('landing.hero_title_p1', 'The Operating System for')}{' '}
          <span style={{ background: 'linear-gradient(90deg, #5e88fc, #ffde5a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {t('landing.hero_title_p2', 'Your Household')}
          </span>
        </h1>
        <p className="text-xl md:text-2xl text-slate-500 dark:text-slate-400 max-w-3xl mx-auto mb-10 font-medium leading-relaxed">
          {t('landing.hero_desc', 'Tasks, calendar, groceries, pets, vehicles, bills — all in one place.')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Button onClick={() => navigate('/login')} size="lg" shape="pill" className="px-10 h-14 text-base font-black flex items-center gap-3 group bg-brand text-white hover:bg-brand-hover">
            {t('landing.get_started_free', 'Get Started Free')} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button onClick={() => document.getElementById('core-features')?.scrollIntoView({ behavior: 'smooth' })} variant="ghost" size="lg" shape="pill" className="px-10 h-14 text-base font-black border border-slate-200 dark:border-slate-700">
            {t('landing.see_all_features', 'See All Features')}
          </Button>
        </div>
        <div className="flex items-center justify-center gap-12 mb-16 text-center">
          {[
            { value: '12+', label: t('landing.stat_modules', 'Modules') },
            { value: '3', label: t('landing.stat_tiers', 'Pricing Tiers') },
            { value: 'AI', label: t('landing.stat_powered', 'Powered') },
          ].map(stat => (
            <div key={stat.label}>
              <div className="text-3xl font-black text-brand">{stat.value}</div>
              <div className="text-xs font-black uppercase tracking-widest text-slate-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Carousel */}
      <section className="px-8 py-8 max-w-4xl mx-auto">
        <h2 className="text-center text-xs font-black uppercase tracking-widest text-slate-400 mb-8">{t('landing.carousel_heading', 'Everything Your Household Needs')}</h2>
        <div
          className="relative overflow-hidden rounded-[2rem] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-brand"
          onMouseEnter={() => { if (timerRef.current) clearInterval(timerRef.current); }}
          onMouseLeave={startAutoplay}
        >
          <div className="flex" style={{ transform: `translateX(-${activeSlide * 100}%)`, transition: 'transform 300ms ease-out' }}>
            {SLIDES.map((slide, i) => {
              const Icon = slide.Icon;
              return (
                <div key={i} className="min-w-full p-12 flex flex-col items-center text-center gap-4">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ backgroundColor: slide.tier === 'PREMIUM' ? 'rgba(255,222,90,0.15)' : 'rgba(94,136,252,0.10)' }}>
                    <Icon className="w-10 h-10" style={{ color: slide.tier === 'PREMIUM' ? '#ffde5a' : '#5e88fc' }} />
                  </div>
                  <h3 className="text-2xl font-black dark:text-white">{slide.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs">{slide.desc}</p>
                  <span className={`px-3 py-1 rounded-full text-2xs font-black uppercase tracking-widest ${
                    slide.tier === 'FREE' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' :
                    slide.tier === 'BASIC' ? 'bg-brand text-white' :
                    'bg-[#ffde5a] text-[#0f1117]'
                  }`}>{t(`landing.tier_${slide.tier.toLowerCase()}`, slide.tier)}</span>
                </div>
              );
            })}
          </div>
          <button onClick={() => setActiveSlide(p => (p - 1 + SLIDES.length) % SLIDES.length)} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700">
            <ChevronLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
          <button onClick={() => setActiveSlide(p => (p + 1) % SLIDES.length)} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700">
            <ChevronRight className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setActiveSlide(i)} className={`rounded-full transition-all ${activeSlide === i ? 'bg-brand w-6 h-2' : 'bg-slate-200 dark:bg-slate-700 w-2 h-2'}`} />
          ))}
        </div>
      </section>

      {/* Core Features Section */}
      <section id="core-features" className="px-8 py-20 max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-black mb-4">{t('landing.core_features_title', 'Core Features')}</h2>
          <p className="text-slate-500 font-medium">{t('landing.core_features_subtitle', 'Everything you need to run your household smoothly.')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { title: t('landing.feat_tasks_title', 'Task Management'), desc: t('landing.feat_tasks_desc', 'Kanban boards, priorities, and due dates for all household tasks.'), Icon: KanbanSquare },
            { title: t('landing.feat_calendar_title', 'Shared Calendar'), desc: t('landing.feat_calendar_desc', 'Unified calendar with event sync and travel buffer detection.'), Icon: CalendarDays },
            { title: t('landing.feat_grocery_title', 'Grocery Lists'), desc: t('landing.feat_grocery_desc', 'Smart grocery tracking with auto-categorization and pantry levels.'), Icon: ShoppingCart },
            { title: t('landing.feat_docs_title', 'Documents Vault'), desc: t('landing.feat_docs_desc', 'Secure document storage with room-by-room home inventory.'), Icon: FileText },
          ].map((feature, i) => (
            <div key={i} className="p-8 rounded-[2rem] border border-slate-200 dark:border-slate-800 hover:border-brand/50 transition-all">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(94,136,252,0.10)' }}>
                <feature.Icon className="w-6 h-6 text-brand" />
              </div>
              <h3 className="text-xl font-black mb-3">{feature.title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="px-8 py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black mb-4">{t('landing.pricing_title', 'Simple, Transparent Pricing')}</h2>
            <p className="text-slate-500 font-medium">{t('landing.pricing_subtitle', "Start free, upgrade when you're ready.")}</p>
          </div>

          {/* Annual billing toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-black uppercase tracking-widest transition-colors ${!isAnnual ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>{t('landing.pricing_monthly', 'Monthly')}</span>
            <button
              onClick={() => setIsAnnual(v => !v)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${isAnnual ? 'bg-brand' : 'bg-slate-200 dark:bg-slate-700'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isAnnual ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
            <span className={`text-sm font-black uppercase tracking-widest transition-colors ${isAnnual ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
              {t('landing.pricing_annual', 'Annual')}
              <span className="ml-2 px-2 py-0.5 bg-[#ffde5a] text-[#0f1117] text-[9px] font-black uppercase tracking-widest rounded-full">{t('landing.pricing_save_20', 'Save 20%')}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 p-8">
              <div className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">{t('landing.pricing_free_name', 'Free')}</div>
              <div className="text-4xl font-black mb-1">$0<span className="text-lg font-bold text-slate-400">/mo</span></div>
              <p className="text-sm text-slate-500 mb-8">{t('landing.pricing_free_desc', 'Everything you need to get started.')}</p>
              <button onClick={() => navigate('/login')} className="w-full py-3 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-black hover:bg-slate-50 dark:hover:bg-slate-800 transition-all mb-8">
                {t('landing.start_for_free', 'Start for Free')}
              </button>
              <ul className="space-y-3 text-sm">
                {[
                  t('landing.pricing_free_f1', 'Tasks & Kanban Board'),
                  t('landing.pricing_free_f2', 'Shared Calendar'),
                  t('landing.pricing_free_f3', 'Grocery Lists'),
                  t('landing.pricing_free_f4', 'Documents Vault'),
                  t('landing.pricing_free_f5', '1 household member'),
                ].map(f => (
                  <li key={f} className="flex items-center gap-3"><Check className="w-4 h-4 text-brand flex-shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border-2 border-brand p-8 shadow-brand-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-brand text-white text-2xs font-black uppercase tracking-widest rounded-full">{t('landing.pricing_most_popular', 'Most Popular')}</div>
              <div className="text-xs font-black uppercase tracking-widest text-brand mb-4">{t('landing.pricing_basic_name', 'Basic')}</div>
              <div className="text-4xl font-black mb-1">
                {isAnnual ? '$7.99' : '$9.99'}<span className="text-lg font-bold text-slate-400">/mo</span>
              </div>
              {isAnnual && <p className="text-[11px] text-slate-400 mb-1">{t('landing.pricing_billed_annually_basic', 'Billed annually ($95.88/yr)')}</p>}
              <p className="text-sm text-slate-500 mb-8">{t('landing.pricing_basic_desc', 'For growing households.')}</p>
              <button onClick={() => navigate('/login')} className="w-full py-3 rounded-full bg-brand text-white text-sm font-black hover:bg-brand-hover transition-all mb-8">
                {t('landing.get_started', 'Get Started')}
              </button>
              <ul className="space-y-3 text-sm">
                {[
                  t('landing.pricing_basic_f1', 'All Free features'),
                  t('landing.pricing_basic_f2', 'Meal Planner + Recipes'),
                  t('landing.pricing_basic_f3', 'Contacts Directory'),
                  t('landing.pricing_basic_f4', 'Pet Management'),
                  t('landing.pricing_basic_f5', 'Vehicle Tracking'),
                  t('landing.pricing_basic_f6', 'Bills & Subscriptions'),
                  t('landing.pricing_basic_f7', 'Up to 6 members'),
                ].map(f => (
                  <li key={f} className="flex items-center gap-3"><Check className="w-4 h-4 text-brand flex-shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 p-8">
              <div className="text-xs font-black uppercase tracking-widest text-amber-500 mb-4">{t('landing.pricing_premium_name', 'Premium')}</div>
              <div className="text-4xl font-black mb-1">
                {isAnnual ? '$15.99' : '$19.99'}<span className="text-lg font-bold text-slate-400">/mo</span>
              </div>
              {isAnnual && <p className="text-[11px] text-slate-400 mb-1">{t('landing.pricing_billed_annually_premium', 'Billed annually ($191.88/yr)')}</p>}
              <p className="text-sm text-slate-500 mb-8">{t('landing.pricing_premium_desc', 'For power users with AI.')}</p>
              <button onClick={() => navigate('/login?intent=upgrade')} className="w-full py-3 rounded-full bg-[#ffde5a] text-[#0f1117] text-sm font-black hover:bg-[#ffe27a] transition-all mb-8">
                {t('landing.go_premium', 'Go Premium')}
              </button>
              <ul className="space-y-3 text-sm">
                {[
                  t('landing.pricing_premium_f1', 'All Basic features'),
                  t('landing.pricing_premium_f2', 'AI Assistant (Gemini)'),
                  t('landing.pricing_premium_f3', 'AI Chore Plans & Meal Gen'),
                  t('landing.pricing_premium_f4', 'Gamification & Rewards'),
                  t('landing.pricing_premium_f5', 'Home Intelligence & Appliances'),
                  t('landing.pricing_premium_f6', 'Up to 8 members'),
                ].map(f => (
                  <li key={f} className="flex items-center gap-3"><Check className="w-4 h-4 text-brand flex-shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-8 py-20">
        <div className="max-w-4xl mx-auto bg-brand rounded-[3.5rem] p-12 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white/20" />
            <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-white/10" />
          </div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-black mb-4">{t('landing.cta_title', 'Ready to simplify your household?')}</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">{t('landing.cta_subtitle', 'Join thousands of households already using EverydayOS to stay organized.')}</p>
            <Button onClick={() => navigate('/login')} className="bg-white text-brand hover:bg-slate-50 rounded-2xl px-12 h-16 font-black text-xl shadow-xl">
              {t('landing.start_for_free', 'Start for Free')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-8 py-16 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 font-black text-xl tracking-tighter mb-4">
                <div className="p-1.5 rounded-xl bg-brand text-white shadow-lg">
                  <Home className="w-5 h-5" />
                </div>
                <span>Everyday<span className="text-brand">OS</span></span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('landing.footer_tagline', 'The operating system for your household.')}</p>
            </div>
            <div>
              <h3 className="font-black mb-4 text-sm uppercase tracking-widest text-slate-400">{t('landing.footer_product', 'Product')}</h3>
              <ul className="space-y-3">
                <li><button onClick={() => document.getElementById('core-features')?.scrollIntoView({ behavior: 'smooth' })} className="text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('landing.footer_features', 'Features')}</button></li>
                <li><Link to="/pricing" className="text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('landing.footer_pricing', 'Pricing')}</Link></li>
                <li><Link to="/login" className="text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('nav.login', 'Login')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-black mb-4 text-sm uppercase tracking-widest text-slate-400">{t('landing.footer_resources', 'Resources')}</h3>
              <ul className="space-y-3">
                <li><span className="text-slate-400 cursor-not-allowed">{t('landing.footer_blog', 'Blog')} <span className="text-xs opacity-60">(coming soon)</span></span></li>
                <li><span className="text-slate-400 cursor-not-allowed">{t('landing.footer_docs', 'Documentation')} <span className="text-xs opacity-60">(coming soon)</span></span></li>
                <li><a href="mailto:support@everydaytheapp.com" className="text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('landing.footer_support', 'Support')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-black mb-4 text-sm uppercase tracking-widest text-slate-400">{t('landing.footer_legal', 'Legal')}</h3>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('landing.footer_privacy', 'Privacy Policy')}</Link></li>
                <li><Link to="/terms" className="text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('landing.footer_terms', 'Terms of Service')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 dark:border-slate-800 pt-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('landing.footer_copyright', '© {{year}} EverydayOS. All rights reserved.', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
};